import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent } from './ui/dialog';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { cn } from '../lib/utils';
import {
  Search, LayoutDashboard, Target, ShoppingBag, Megaphone, Heart,
  Headphones, Briefcase, Globe, BarChart2, Settings, Users,
  ArrowRight, FileText, Zap, ScanLine, TrendingUp, Share2, PenTool,
  Mail, Phone, UserPlus, CalendarDays, Award, Gift, MessageSquare,
  Ticket, BookOpen, ClipboardList, GanttChart, FolderOpen, Bell,
  Mic2, Shield, Lock, Star, Command
} from 'lucide-react';

const ICON_MAP = {
  LayoutDashboard, Target, ShoppingBag, Megaphone, Heart, Headphones,
  Briefcase, Globe, BarChart2, Settings, Users, FileText, Zap, ScanLine,
  TrendingUp, Share2, PenTool, Mail, Phone, UserPlus, CalendarDays,
  Award, Gift, MessageSquare, Ticket, BookOpen, ClipboardList, GanttChart,
  FolderOpen, Bell, Mic2, Shield, Lock, Star, Search
};

const SEARCH_ITEMS = [
  // Overview
  { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard', section: 'Overview', keywords: 'home overview stats kpi' },
  { label: 'Admin Portal', path: '/admin', icon: 'Users', section: 'Overview', keywords: 'admin manage users operators' },
  // Lead Gen
  { label: 'Lead Pipeline (CRM)', path: '/leads/pipeline', icon: 'Target', section: 'Lead Generation', keywords: 'crm kanban leads contacts prospects' },
  { label: 'Lead Capture Forms', path: '/leads/capture-forms', icon: 'FileText', section: 'Lead Generation', keywords: 'forms capture landing signup' },
  { label: 'Business Card Scanner', path: '/leads/card-scanner', icon: 'ScanLine', section: 'Lead Generation', keywords: 'ocr scan card contact import' },
  { label: 'QR Code Generator', path: '/leads/qr-generator', icon: 'Zap', section: 'Lead Generation', keywords: 'qr code generate link share' },
  { label: 'Lead Scoring & AI', path: '/leads/ai-insights', icon: 'TrendingUp', section: 'Lead Generation', keywords: 'ai scoring insights intelligence' },
  // Sales
  { label: 'Product & Service Catalog', path: '/sales/catalog', icon: 'ShoppingBag', section: 'Sales', keywords: 'products services catalog inventory pricing' },
  { label: 'Proposals & Quotes', path: '/sales/proposals', icon: 'FileText', section: 'Sales', keywords: 'proposals quotes estimates invoices' },
  { label: 'Promotions & Deals', path: '/sales/promotions', icon: 'Gift', section: 'Sales', keywords: 'promotions deals coupons discounts promo codes' },
  { label: 'Sales Pipeline', path: '/sales/pipeline', icon: 'TrendingUp', section: 'Sales', keywords: 'deals pipeline stages negotiation close' },
  { label: 'Commission Tracker', path: '/sales/commissions', icon: 'Award', section: 'Sales', keywords: 'commissions payouts sales rep earnings' },
  // Marketing
  { label: 'Social Media', path: '/social', icon: 'Share2', section: 'Marketing', keywords: 'social media facebook instagram twitter posts' },
  { label: 'Content Studio', path: '/content', icon: 'PenTool', section: 'Marketing', keywords: 'content blog articles writing ai generate' },
  { label: 'Email Marketing', path: '/email', icon: 'Mail', section: 'Marketing', keywords: 'email campaigns newsletters subscribers' },
  { label: 'Paid Ads', path: '/ads', icon: 'Megaphone', section: 'Marketing', keywords: 'ads advertising google facebook campaigns budget' },
  { label: 'SEO Center', path: '/seo', icon: 'Search', section: 'Marketing', keywords: 'seo keywords rankings search optimization' },
  { label: 'SMS & WhatsApp', path: '/marketing/sms-whatsapp', icon: 'Phone', section: 'Marketing', keywords: 'sms whatsapp messaging text campaigns' },
  { label: 'Influencer & Affiliates', path: '/marketing/affiliates', icon: 'UserPlus', section: 'Marketing', keywords: 'influencer affiliate partner ambassador' },
  { label: 'Events & Webinars', path: '/marketing/events', icon: 'CalendarDays', section: 'Marketing', keywords: 'events webinars conference meetup virtual' },
  // Retention
  { label: 'Loyalty Program', path: '/retention/loyalty', icon: 'Award', section: 'Retention', keywords: 'loyalty points tiers members rewards' },
  { label: 'Birthday & Anniversary', path: '/retention/celebrations', icon: 'Gift', section: 'Retention', keywords: 'birthday anniversary celebrations greetings' },
  { label: 'Referral Program', path: '/retention/referrals', icon: 'UserPlus', section: 'Retention', keywords: 'referral codes share invite conversion' },
  { label: 'Win-Back Campaigns', path: '/retention/winback', icon: 'Heart', section: 'Retention', keywords: 'winback churn reactivation retention' },
  // Service
  { label: 'Voice Receptionist', path: '/voice-receptionist', icon: 'Mic2', section: 'Service', keywords: 'voice receptionist ai phone call ivr' },
  { label: 'Help Desk & Ticketing', path: '/service/helpdesk', icon: 'Ticket', section: 'Service', keywords: 'helpdesk tickets support issues bugs' },
  { label: 'Live Chat', path: '/service/live-chat', icon: 'MessageSquare', section: 'Service', keywords: 'chat live messaging real-time' },
  { label: 'Knowledge Base', path: '/service/knowledge-base', icon: 'BookOpen', section: 'Service', keywords: 'knowledge base articles faq help docs' },
  { label: 'Feedback & Surveys', path: '/service/surveys', icon: 'ClipboardList', section: 'Service', keywords: 'surveys feedback csat nps satisfaction' },
  // Operations
  { label: 'Team & Employees', path: '/operations/employees', icon: 'Users', section: 'Operations', keywords: 'employees team staff hr people' },
  { label: 'KPI & Performance', path: '/operations/performance', icon: 'TrendingUp', section: 'Operations', keywords: 'kpi performance metrics goals targets' },
  { label: 'Tasks & Projects', path: '/operations/tasks', icon: 'ClipboardList', section: 'Operations', keywords: 'tasks projects kanban board todo' },
  { label: 'Calendar', path: '/operations/calendar', icon: 'CalendarDays', section: 'Operations', keywords: 'calendar events meetings schedule' },
  { label: 'Document Vault', path: '/operations/documents', icon: 'FolderOpen', section: 'Operations', keywords: 'documents files storage vault contracts' },
  { label: 'Workflow Automation', path: '/automation-center', icon: 'GanttChart', section: 'Operations', keywords: 'workflows automation processes triggers automation center' },
  { label: 'Announcements', path: '/operations/announcements', icon: 'Bell', section: 'Operations', keywords: 'announcements news notices updates' },
  // Website
  { label: 'Website Manager', path: '/website', icon: 'Globe', section: 'Website', keywords: 'website manage pages domain' },
  { label: 'ADA Compliance', path: '/ada', icon: 'Shield', section: 'Website', keywords: 'ada accessibility compliance wcag' },
  { label: 'Landing Page Builder', path: '/website/landing-pages', icon: 'FileText', section: 'Website', keywords: 'landing pages builder templates convert' },
  // Analytics
  { label: 'Analytics & Reports', path: '/analytics', icon: 'BarChart2', section: 'Analytics', keywords: 'analytics reports data insights charts' },
  { label: 'Reputation & Reviews', path: '/reputation', icon: 'Star', section: 'Analytics', keywords: 'reputation reviews ratings google yelp' },
  { label: 'Business Intelligence', path: '/analytics/intelligence', icon: 'TrendingUp', section: 'Analytics', keywords: 'intelligence bi predictions ai forecast' },
  { label: 'Custom Reports', path: '/analytics/reports', icon: 'FileText', section: 'Analytics', keywords: 'reports builder custom export schedule' },
  // Settings
  { label: 'Account Settings', path: '/settings', icon: 'Settings', section: 'Settings', keywords: 'settings account profile preferences' },
  { label: 'Billing & Subscription', path: '/settings/billing', icon: 'Award', section: 'Settings', keywords: 'billing subscription plan pricing payment' },
  { label: 'Security Settings', path: '/settings/security', icon: 'Lock', section: 'Settings', keywords: 'security password 2fa authentication' },
];

const SECTION_COLORS = {
  'Overview': 'text-emerald-600', 'Lead Generation': 'text-blue-600', 'Sales': 'text-purple-600',
  'Marketing': 'text-orange-600', 'Retention': 'text-pink-600', 'Service': 'text-teal-600',
  'Operations': 'text-amber-600', 'Website': 'text-indigo-600', 'Analytics': 'text-cyan-600',
  'Settings': 'text-gray-600',
};

export default function GlobalSearch({ open, onOpenChange }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  const results = query.trim().length > 0
    ? SEARCH_ITEMS.filter(item => {
        const q = query.toLowerCase();
        return item.label.toLowerCase().includes(q) ||
               item.section.toLowerCase().includes(q) ||
               item.keywords.includes(q);
      }).slice(0, 12)
    : SEARCH_ITEMS.slice(0, 8);

  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => { setSelectedIndex(0); }, [query]);

  // Scroll selected into view
  useEffect(() => {
    if (listRef.current) {
      const el = listRef.current.children[selectedIndex];
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleSelect = useCallback((item) => {
    navigate(item.path);
    onOpenChange(false);
  }, [navigate, onOpenChange]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    }
  };

  // Group results by section
  const grouped = {};
  results.forEach(item => {
    if (!grouped[item.section]) grouped[item.section] = [];
    grouped[item.section].push(item);
  });

  let flatIdx = 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl p-0 gap-0 overflow-hidden border-gray-200 shadow-2xl" data-testid="global-search-dialog" aria-describedby={undefined}>
        <div className="sr-only" role="heading" aria-level="2">Global Search</div>
        {/* Search Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <Search className="h-5 w-5 text-gray-400 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, features, settings..."
            className="flex-1 text-sm bg-transparent outline-none placeholder:text-gray-400 text-gray-800"
            data-testid="global-search-input"
          />
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono text-gray-400 bg-gray-100 rounded border border-gray-200">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[380px] overflow-y-auto py-1" data-testid="global-search-results">
          {results.length === 0 ? (
            <div className="py-12 text-center">
              <Search className="h-10 w-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No results for "{query}"</p>
              <p className="text-xs text-gray-400 mt-1">Try searching for a page name or feature</p>
            </div>
          ) : (
            Object.entries(grouped).map(([section, items]) => (
              <div key={section}>
                <div className="px-4 py-1.5">
                  <span className={`text-[10px] font-semibold uppercase tracking-wider ${SECTION_COLORS[section] || 'text-gray-400'}`}>{section}</span>
                </div>
                {items.map(item => {
                  const idx = flatIdx++;
                  const isSelected = idx === selectedIndex;
                  const IconComp = ICON_MAP[item.icon] || FileText;
                  return (
                    <button
                      key={item.path}
                      onClick={() => handleSelect(item)}
                      onMouseEnter={() => setSelectedIndex(idx)}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors",
                        isSelected ? "bg-gray-100" : "hover:bg-gray-50"
                      )}
                      data-testid={`search-result-${item.path.replace(/\//g, '-').slice(1)}`}
                    >
                      <div className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                        isSelected ? "bg-emerald-100" : "bg-gray-100"
                      )}>
                        <IconComp className={cn("h-4 w-4", isSelected ? "text-emerald-600" : "text-gray-500")} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={cn("text-sm", isSelected ? "font-semibold text-gray-900" : "text-gray-700")}>{item.label}</p>
                      </div>
                      {isSelected && <ArrowRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-gray-200 rounded text-[9px] font-mono">&uarr;&darr;</kbd> Navigate</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-gray-200 rounded text-[9px] font-mono">&crarr;</kbd> Open</span>
            <span className="flex items-center gap-1"><kbd className="px-1 py-0.5 bg-gray-200 rounded text-[9px] font-mono">Esc</kbd> Close</span>
          </div>
          <span className="text-[10px] text-gray-300">{results.length} results</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
